import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  X, ArrowRight, ArrowLeft, Check, Wallet, TrendingUp, TrendingDown,
  CreditCard, Repeat, Calendar, Home, Car, Wifi, Smartphone, 
  ShoppingCart, Zap, Droplets, Trash2, Plus, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Catégories suggérées pour chaque type
const INCOME_SUGGESTIONS = [
  { name: 'Salaire', icon: Wallet, amount: 2000, day: 28 },
  { name: 'Allocations', icon: TrendingUp, amount: 300, day: 5 },
  { name: 'Revenus locatifs', icon: Home, amount: 800, day: 1 },
  { name: 'Freelance', icon: Zap, amount: 500, day: 15 },
];

const EXPENSE_SUGGESTIONS = [
  { name: 'Loyer', icon: Home, amount: 800, day: 1, category: 'Logement' },
  { name: 'Électricité', icon: Zap, amount: 80, day: 15, category: 'Énergie' },
  { name: 'Eau', icon: Droplets, amount: 40, day: 15, category: 'Énergie' },
  { name: 'Internet', icon: Wifi, amount: 30, day: 10, category: 'Abonnements' },
  { name: 'Téléphone', icon: Smartphone, amount: 20, day: 5, category: 'Abonnements' },
  { name: 'Assurance auto', icon: Car, amount: 50, day: 1, category: 'Assurance' },
  { name: 'Crédit voiture', icon: CreditCard, amount: 250, day: 5, category: 'Crédit' },
  { name: 'Courses', icon: ShoppingCart, amount: 400, day: 1, category: 'Alimentation' },
];

const InitialSetupWizard = ({ open, onClose, calendarId, onComplete }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Form state
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [newIncome, setNewIncome] = useState({ name: '', amount: '', day: 1 });
  const [newExpense, setNewExpense] = useState({ name: '', amount: '', day: 1, category: '' });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setStep(0);
      setIncomes([]);
      setExpenses([]);
      setShowSuccess(false);
    }
  }, [open]);

  const addIncome = (income) => {
    if (income.name && income.amount) {
      setIncomes([...incomes, { ...income, id: Date.now() }]);
      setNewIncome({ name: '', amount: '', day: 1 });
    }
  };

  const addExpense = (expense) => {
    if (expense.name && expense.amount) {
      setExpenses([...expenses, { ...expense, id: Date.now() }]);
      setNewExpense({ name: '', amount: '', day: 1, category: '' });
    }
  };

  const removeIncome = (id) => {
    setIncomes(incomes.filter(i => i.id !== id));
  };

  const removeExpense = (id) => {
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create recurring operations for all incomes
      for (const income of incomes) {
        await axios.post(
          `${API}/recurring-operations?calendar_id=${calendarId}`,
          {
            name: income.name,
            type: 'credit',
            amount: parseFloat(income.amount),
            category: 'Revenus',
            day_of_month: parseInt(income.day),
            start_date: new Date().toISOString().split('T')[0],
            active: true
          },
          { withCredentials: true }
        );
      }

      // Create recurring operations for all expenses
      for (const expense of expenses) {
        await axios.post(
          `${API}/recurring-operations?calendar_id=${calendarId}`,
          {
            name: expense.name,
            type: 'debit',
            amount: parseFloat(expense.amount),
            category: expense.category || 'Dépenses fixes',
            day_of_month: parseInt(expense.day),
            start_date: new Date().toISOString().split('T')[0],
            active: true
          },
          { withCredentials: true }
        );
      }

      // Generate operations for current month
      const today = new Date();
      await axios.post(
        `${API}/generate-recurring?calendar_id=${calendarId}&year=${today.getFullYear()}&month=${today.getMonth() + 1}`,
        {},
        { withCredentials: true }
      );

      setShowSuccess(true);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 2500);

    } catch (error) {
      console.error('Error creating initial setup:', error);
      toast.error('Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const totalIncome = incomes.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
  const totalExpense = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const monthlyBalance = totalIncome - totalExpense;

  const steps = [
    { title: 'Bienvenue', icon: Sparkles },
    { title: 'Revenus', icon: TrendingUp },
    { title: 'Dépenses fixes', icon: TrendingDown },
    { title: 'Récapitulatif', icon: Check },
  ];

  if (!open) return null;

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6 text-center">
            <div className="text-6xl mb-4">🚀</div>
            <h2 className="text-2xl font-bold text-gray-900">
              Configurons ton budget !
            </h2>
            <p className="text-gray-600">
              En quelques étapes, on va configurer tes revenus et dépenses fixes 
              pour que tu puisses te projeter sur les mois à venir.
            </p>
            
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-200">
              <p className="text-sm text-teal-800">
                <strong>💡 Astuce :</strong> Toutes les opérations créées ici seront 
                automatiquement intégrées à ton calendrier et visibles sur les mois suivants.
              </p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <TrendingUp className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-900">Tes revenus mensuels</h2>
              <p className="text-sm text-gray-500">Salaire, allocations, revenus locatifs...</p>
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {INCOME_SUGGESTIONS.map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => addIncome({ ...sug, id: Date.now() })}
                  className="px-3 py-1.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  + {sug.name}
                </button>
              ))}
            </div>

            {/* Liste des revenus ajoutés */}
            {incomes.length > 0 && (
              <div className="space-y-2 mb-4">
                {incomes.map((income) => (
                  <div key={income.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div>
                      <span className="font-medium">{income.name}</span>
                      <span className="text-xs text-gray-500 ml-2">le {income.day} du mois</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-emerald-600">+{income.amount}€</span>
                      <button onClick={() => removeIncome(income.id)} className="text-gray-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulaire d'ajout */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Nom</Label>
                  <Input
                    placeholder="Ex: Salaire"
                    value={newIncome.name}
                    onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Jour</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={newIncome.day}
                    onChange={(e) => setNewIncome({ ...newIncome, day: e.target.value })}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <div className="flex-1">
                  <Label className="text-xs">Montant (€)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                    className="h-9"
                  />
                </div>
                <Button 
                  type="button" 
                  onClick={() => addIncome(newIncome)}
                  disabled={!newIncome.name || !newIncome.amount}
                  className="mt-5 bg-emerald-600"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="text-center pt-2">
              <span className="text-sm text-gray-500">Total revenus : </span>
              <span className="font-bold text-emerald-600">{totalIncome.toFixed(2)}€/mois</span>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <TrendingDown className="h-10 w-10 text-rose-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-900">Tes dépenses fixes</h2>
              <p className="text-sm text-gray-500">Loyer, factures, abonnements, crédits...</p>
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {EXPENSE_SUGGESTIONS.slice(0, 6).map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => addExpense({ ...sug, id: Date.now() })}
                  className="px-3 py-1.5 text-xs rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-all"
                >
                  + {sug.name}
                </button>
              ))}
            </div>

            {/* Liste des dépenses ajoutées */}
            {expenses.length > 0 && (
              <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                {expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-200">
                    <div>
                      <span className="font-medium">{expense.name}</span>
                      <span className="text-xs text-gray-500 ml-2">le {expense.day} du mois</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-rose-600">-{expense.amount}€</span>
                      <button onClick={() => removeExpense(expense.id)} className="text-gray-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulaire d'ajout */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Nom</Label>
                  <Input
                    placeholder="Ex: Loyer"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Jour</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={newExpense.day}
                    onChange={(e) => setNewExpense({ ...newExpense, day: e.target.value })}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <div className="flex-1">
                  <Label className="text-xs">Montant (€)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    className="h-9"
                  />
                </div>
                <Button 
                  type="button" 
                  onClick={() => addExpense(newExpense)}
                  disabled={!newExpense.name || !newExpense.amount}
                  className="mt-5 bg-rose-600"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="text-center pt-2">
              <span className="text-sm text-gray-500">Total dépenses : </span>
              <span className="font-bold text-rose-600">{totalExpense.toFixed(2)}€/mois</span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <Check className="h-10 w-10 text-teal-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-900">Récapitulatif</h2>
              <p className="text-sm text-gray-500">Vérifie avant de valider</p>
            </div>

            {/* Résumé */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
                <TrendingUp className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
                <div className="text-xs text-emerald-600">Revenus</div>
                <div className="text-xl font-bold text-emerald-700">+{totalIncome.toFixed(0)}€</div>
                <div className="text-xs text-gray-500">{incomes.length} source(s)</div>
              </div>
              <div className="bg-rose-50 rounded-xl p-4 border border-rose-200 text-center">
                <TrendingDown className="h-6 w-6 text-rose-500 mx-auto mb-1" />
                <div className="text-xs text-rose-600">Dépenses</div>
                <div className="text-xl font-bold text-rose-700">-{totalExpense.toFixed(0)}€</div>
                <div className="text-xs text-gray-500">{expenses.length} dépense(s)</div>
              </div>
            </div>

            {/* Balance mensuelle */}
            <div className={`rounded-xl p-4 border-2 text-center ${
              monthlyBalance >= 0 
                ? 'bg-teal-50 border-teal-300' 
                : 'bg-amber-50 border-amber-300'
            }`}>
              <div className="text-sm text-gray-600 mb-1">Balance mensuelle estimée</div>
              <div className={`text-3xl font-bold ${
                monthlyBalance >= 0 ? 'text-teal-600' : 'text-amber-600'
              }`}>
                {monthlyBalance >= 0 ? '+' : ''}{monthlyBalance.toFixed(0)}€
              </div>
              {monthlyBalance >= 0 ? (
                <p className="text-xs text-teal-600 mt-1">🎉 Super ! Tu es dans le positif.</p>
              ) : (
                <p className="text-xs text-amber-600 mt-1">⚠️ Attention, tu dépenses plus que tu gagnes.</p>
              )}
            </div>

            {/* Impact calendrier */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-2">
                <Calendar className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Impact sur ton calendrier</p>
                  <p className="text-xs text-blue-600">
                    Ces opérations seront visibles immédiatement dans ton calendrier 
                    et automatiquement reproduites chaque mois.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Success screen
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Check className="h-10 w-10 text-white" />
          </motion.div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Configuration terminée !</h3>
          <p className="text-gray-600">
            Ton budget est prêt. Toutes tes opérations ont été ajoutées au calendrier.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">Configuration initiale</h2>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Progress */}
          <div className="flex gap-1 mt-3">
            {steps.map((s, idx) => (
              <div
                key={idx}
                className={`flex-1 h-1 rounded-full transition-all ${
                  idx <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
          <div className="text-xs text-white/80 mt-1">
            Étape {step + 1}/{steps.length} - {steps[step].title}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1"
              disabled={loading}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          )}
          
          {step < steps.length - 1 ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600"
            >
              Suivant
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || (incomes.length === 0 && expenses.length === 0)}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Valider
                </>
              )}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InitialSetupWizard;
